import { ApolloError } from "@apollo/client";
import type {
  GraphQLError,
  GraphQLErrorExtensions,
  GraphQLErrorOptions,
} from "graphql";
import type { FieldValues, Path } from "react-hook-form";
import { isEqual, merge } from "lodash";

export type FieldErrorsByKey = { [key: string]: string[] };

export type GetFieldErrorsForError = (
  error: GraphQLError,
) => FieldErrorsByKey | undefined | void;

export type FilterGraphQLErrors = (
  graphQLErrors: readonly GraphQLError[],
) => readonly GraphQLError[];

export type PartitionedFieldErrorsTuple = [
  nonFieldErrors?: GraphQLError[],
  fieldErrors?: FieldErrorsByKey,
];

type MakeGraphQLErrorProcessorOptions = {
  /** Determines whether each retained GraphQL error is a field or non-field
   * error. Field errors are returned under their form field keys. */
  getFieldErrorsForError: GetFieldErrorsForError;
  /** Optionally removes endpoint-specific secondary errors before the retained
   * errors are partitioned. */
  filterGraphQLErrors?: FilterGraphQLErrors;
};

export function makeGraphQLErrorProcessor({
  filterGraphQLErrors,
  getFieldErrorsForError,
}: MakeGraphQLErrorProcessorOptions) {
  return function processApolloError(
    error: Error,
  ): PartitionedFieldErrorsTuple {
    if (!(error instanceof ApolloError)) return [];

    if (!error.graphQLErrors?.length) return [];

    const graphQLErrors = filterGraphQLErrors
      ? filterGraphQLErrors(error.graphQLErrors)
      : error.graphQLErrors;

    const nonFieldErrors: GraphQLError[] = [];

    const byKey = graphQLErrors.reduce<FieldErrorsByKey>((acc, gqlError) => {
      const fieldErrors = getFieldErrorsForError(gqlError);

      if (fieldErrors && Object.keys(fieldErrors).length) {
        merge(acc, fieldErrors);
      } else {
        nonFieldErrors.push(gqlError);
      }

      return acc;
    }, {});

    if (!Object.keys(byKey).length) return [nonFieldErrors];

    return [nonFieldErrors, byKey];
  };
}

export type ExtendedGraphQLError<Extensions extends GraphQLErrorExtensions> =
  GraphQLError & { extensions: Extensions };

export type ExtendedGraphQLErrorOptions<
  Extensions extends GraphQLErrorExtensions,
> = GraphQLErrorOptions & { extensions: Extensions };

export type ErrorMatcher<Extensions extends GraphQLErrorExtensions> = Partial<
  ExtendedGraphQLErrorOptions<Partial<Extensions>>
>;

export type SimpleFieldErrorMatcher<
  Extensions extends GraphQLErrorExtensions,
  TFieldValues extends FieldValues,
> = {
  field: Path<TFieldValues>;
  matcher: ErrorMatcher<Extensions>;
};

export type FieldsGetterReturn<TFieldValues extends FieldValues> =
  | Partial<Record<Path<TFieldValues>, string[]>>
  | undefined;

export type FieldsGetterErrorMatcher<
  Extensions extends GraphQLErrorExtensions,
  TFieldValues extends FieldValues,
> = {
  getFieldErrors: (error: GraphQLError) => FieldsGetterReturn<TFieldValues>;
  matcher: ErrorMatcher<Extensions>;
};

export type FieldErrorMatcher<
  Extensions extends GraphQLErrorExtensions,
  TFieldValues extends FieldValues,
> =
  | SimpleFieldErrorMatcher<Extensions, TFieldValues>
  | FieldsGetterErrorMatcher<Extensions, TFieldValues>;

function isSimpleFieldErrorMatcher<
  Extensions extends GraphQLErrorExtensions,
  TFieldValues extends FieldValues,
>(
  matcher: FieldErrorMatcher<Extensions, TFieldValues>,
): matcher is SimpleFieldErrorMatcher<Extensions, TFieldValues> {
  return "field" in matcher;
}

type MakeFieldErrorMatcherOptions<Extensions extends GraphQLErrorExtensions> = {
  getIsExtensionsMatch: (
    extensions: GraphQLErrorExtensions,
    extensionsToMatch: Partial<Extensions>,
  ) => boolean;
};

export function makeFieldErrorMatcher<
  Extensions extends GraphQLErrorExtensions,
  TFieldValues extends FieldValues,
>(
  matchers: FieldErrorMatcher<Extensions, TFieldValues>[],
  { getIsExtensionsMatch }: MakeFieldErrorMatcherOptions<Extensions>,
) {
  return function getFieldErrorsForError(error: GraphQLError) {
    return matchers.reduce<FieldErrorsByKey>((acc, fieldMatcher) => {
      const { matcher } = fieldMatcher;

      const isErrorAttrsEqual = (
        ["nodes", "source", "positions", "path", "originalError"] as const
      ).every((attr) => {
        if (!(attr in matcher) || typeof matcher[attr] === undefined) {
          return true;
        }
        return isEqual(matcher[attr], error[attr]);
      });

      if (!isErrorAttrsEqual) return acc;

      if (
        matcher.extensions &&
        !getIsExtensionsMatch(error.extensions, matcher.extensions)
      ) {
        return acc;
      }

      if (isSimpleFieldErrorMatcher(fieldMatcher)) {
        merge(acc, { [fieldMatcher.field]: [error.message] });
      } else {
        const fieldErrors = fieldMatcher.getFieldErrors(error);
        if (fieldErrors) merge(acc, fieldErrors);
      }

      return acc;
    }, {});
  };
}

export type PrefixFields<
  F extends string,
  P extends string | undefined = undefined,
> = P extends undefined ? F : P extends string ? `${P}.${F}` : never;

export type EmptyPrefixedFieldValues<
  F extends string,
  P extends string | undefined = undefined,
> = Record<PrefixFields<F, P>, unknown>;
